<script setup lang="ts">
import type { EventVault } from "@/models"
import { onUnmounted, watchEffect } from "vue"
import { GuestHeroImage } from "@/components"
import { Banner, Card, Icon, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"
import { useTemplateStore } from "@/stores/templateStore"
import { convertDateTimezone } from "@/utils"

interface InvitationPreviewProps {
  eventVault?: EventVault
  invitationTitle?: string
  invitationDescription?: string
  disabled?: boolean
}

const props = defineProps<InvitationPreviewProps>()

const { d, t } = useTranslation()

const templateStore = useTemplateStore()

watchEffect(() => {
  const eventTemplate = props.eventVault?.eventTemplate
  const templateCss = eventTemplate?.templateCss

  if (!templateCss) {
    templateStore.clearTemplate()
    return
  }

  templateStore.setTemplate(eventTemplate)
  templateStore.applyTemplateStyles()
})

onUnmounted(() => templateStore.clearTemplate())
</script>

<template>
  <link
    v-if="templateStore.baseFontValue"
    :href="`https://fonts.googleapis.com/css2?family=${templateStore.baseFontValue}&display=swap`"
    rel="stylesheet"
  >
  <link
    v-if="templateStore.headingFontValue"
    :href="`https://fonts.googleapis.com/css2?family=${templateStore.headingFontValue}&display=swap`"
    rel="stylesheet"
  >

  <div>
    <div class="flex items-center gap-2 mb-4">
      <Icon icon="EyeOn" class="text-nav-active" />
      <h3 class="font-comfortaa font-bold text-lg">
        {{ t("invitation_preview_page.title") }}
      </h3>
    </div>

    <div class="bg-gray-50 rounded-xl p-4 flex flex-col h-fit" :class="{ 'opacity-50 pointer-events-none': disabled }">
      <div v-if="disabled">
        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 mb-4">
          {{ t("invitation_preview_page.disabled") }}
        </span>
      </div>

      <Banner class="mb-4 text-sm">
        <template #description>
          <i18n-t keypath="invitation_preview_page.pro_tip_message" scope="global">
            <template #proTipLabel>
              <strong>{{ t("invitation_preview_page.pro_tip_label") }}</strong>
            </template>
            <template #eventDetailsLink>
              <a class="underline font-semibold" :href="`/event/${eventVault?.id}/edit`" target="_blank">
                {{ t("manage_invitation_page.form.event_details_link") }}
              </a>
            </template>
          </i18n-t>
        </template>
      </Banner>

      <div class="overflow-hidden">
        <Card variant="secondary" :with-padding="false" class="guest-layout">
          <div class="grid lg:grid-cols-[1fr_1fr] min-h-80">
            <div class="p-4 lg:p-6 flex flex-col gap-4 font-montserrat guest-layout">
              <h1 class="text-xl lg:text-2xl font-comfortaa">
                {{ invitationTitle || t("invitation_preview_page.add_title") }}
              </h1>
              <div class="text-sm">
                <ReadMore :lines="6" :content="invitationDescription || t('invitation_preview_page.add_message')" />
              </div>
              <div class="flex flex-col gap-3 font-medium shrink-0 border border-secondary-card-text rounded-lg p-3 text-sm">
                <div v-if="eventVault?.location?.formattedAddress">
                  <div class="flex items-end gap-2">
                    <Icon class="shrink-0" icon="Location" size="sm" />
                    <span class="text-xs">{{ t("invitation_preview_page.location") }}</span>
                  </div>
                  <div class="ml-6 font-semibold text-xs">
                    {{ eventVault.location.displayAddress }}
                    <br>
                    <a
                      v-if="eventVault?.location?.googleId"
                      :href="`https://www.google.com/maps/place/?q=place_id:${eventVault?.location?.googleId}`"
                      class="flex items-center gap-1 text-xs underline font-normal hover:text-primary w-fit"
                      target="_blank"
                    >
                      {{ t("invitation_preview_page.google_maps") }}
                      <Icon icon="ArrowRight" class="shrink-0" size="sm" />
                    </a>
                  </div>
                </div>
                <div>
                  <div class="flex items-end gap-2">
                    <Icon class="shrink-0" icon="Calendar" size="sm" />
                    <span class="text-xs">{{ eventVault?.timezone ? t("invitation_preview_page.date_time") : t("invitation_preview_page.date") }}</span>
                  </div>
                  <div class="ml-6 font-semibold text-xs">
                    {{ eventVault?.timezone ? d(convertDateTimezone(eventVault.eventDate ?? new Date(), eventVault.timezone), "long") : d(eventVault?.eventDate ?? new Date(), "short") }}
                  </div>
                </div>
              </div>
            </div>

            <GuestHeroImage :cover-image="eventVault?.coverImageUrl" />
          </div>
        </Card>
      </div>
    </div>

    <p class="font-montserrat mt-4">
      {{ t("invitation_preview_page.ready_to_share") }}
    </p>
  </div>
</template>

<style scoped>
.guest-layout h1,
.guest-layout h2,
.guest-layout h3,
.guest-layout h4,
.guest-layout h5,
.guest-layout h6 {
  font-family: v-bind("templateStore.headingFontLabel"), "Open Sans", Montserrat, sans-serif !important;
  margin: 0;
  padding: 0;
}

.guest-layout * {
  font-family: v-bind("templateStore.baseFontLabel"), "Open Sans", Montserrat, sans-serif !important;
}
</style>
